import clsx from 'clsx'
import Link from 'next/link'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import ProposalDates, {
  ProposalDatesProps,
} from './proposal-dates/ProposalDates'
import { ProposalHouseEquipments } from './proposal-house-equipments/ProposalHouseEquipments'
import {
  ProposalInformationHouseType,
  ProposalInformationServiceProviderType,
} from './ProposalInformation.types'
import { getHouseTypeLabel } from './utils/utils'
import { DurationFrom, IDurationFromProps } from '../../duration-from'
import { ClientProposalFragment } from '../../../../lib/graphql'
import { useDomainFromRouter } from '../../../../lib/hooks'
import { cn } from '../../../../shadcn/utils'
import { computeHouseAppExplorerUrl } from '../../../../utils/house-explorer-url-params.helper'
import { IconSvg } from '../../../atoms'
import { useSeminarAppContext } from '../../../contexts'
import { Capacity } from '../../../molecules'
import { Rating } from '../../../molecules/rating/Rating'

interface ProposalInformationProps {
  clientProposal: Pick<
    ClientProposalFragment,
    | '_id'
    | 'isLocked'
    | 'houseBookingType'
    | 'adults'
    | 'clientRequestId'
    | 'type'
    | 'arrivalHour'
    | 'departureHour'
    | 'isClientProposalCancelled'
  >
  dates?: null | ProposalDatesProps
  durationFromProps?: IDurationFromProps
  house?: ProposalInformationHouseType
  serviceProvider?: ProposalInformationServiceProviderType
}
const ProposalInformation = ({
  clientProposal,
  dates,
  durationFromProps,
  house,
  serviceProvider,
}: ProposalInformationProps) => {
  const intl = useIntl()
  const { isSeminar } = useSeminarAppContext()
  const domain = useDomainFromRouter()
  const { adults, type, arrivalHour, departureHour } = clientProposal

  const [displayAdditionalInfo, setDisplayAdditionalInfo] =
    useState<boolean>(false)
  const houseEquipments =
    house && (house?.equipments?.length || 0) > 0 ? house.equipments : null

  const clientProposalHours =
    arrivalHour && departureHour ? { arrivalHour, departureHour } : null

  const address =
    house && house.address?.department && house.address.region
      ? `${house.address?.department}, ${house.address?.region}`
      : serviceProvider &&
          serviceProvider.address?.department &&
          serviceProvider.address?.region
        ? `${serviceProvider.address?.department}, ${serviceProvider.address?.region}`
        : null

  return (
    <div
      className={cn(
        s.informations,
        clientProposal.isClientProposalCancelled && '!line-through',
      )}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          justifyContent: 'stretch',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <div className={s.titleAndGoogleRating}>
          <div className={s.title}>
            {house ? (
              <Link
                href={computeHouseAppExplorerUrl({ house, isSeminar, domain })}
                rel='noopener noreferrer'
                target='_blank'
              >
                {house.title}
              </Link>
            ) : serviceProvider ? (
              serviceProvider.title
            ) : null}

            <div
              className={clsx(s.additionalInfoIcon, {
                [s.additionalInfoIconDisplayed]: displayAdditionalInfo,
              })}
              onClick={(e) => {
                e.stopPropagation()
                setDisplayAdditionalInfo(!displayAdditionalInfo)
              }}
            >
              <IconSvg.ArrowLeft
                color='black'
                style={{ height: '25px', width: '25px' }}
              />
            </div>
          </div>
          <Rating
            rating={house?.googleRating?.rating}
            userRatingsTotal={house?.googleRating?.userRatingsTotal}
          />
        </div>
      </div>

      <div className={clsx(s.informationWrapper, s.datesStatusWrapper)}>
        {dates ? (
          <ProposalDates {...dates} hours={clientProposalHours} />
        ) : null}
      </div>
      <div
        className={clsx(s.informationWrapper, s.locationDistanceWrapper, {
          [s.informationWrapperAdditionalInfo]: displayAdditionalInfo,
        })}
      >
        <div className={s.subTitle}>{address}</div>

        {durationFromProps && house ? (
          <DurationFrom
            city={house.travelCity}
            travelTime={house.travelTime || []}
            className={s.distance}
            {...durationFromProps}
          />
        ) : null}
      </div>

      <div
        className={clsx(s.informationWrapper, {
          [s.informationWrapperAdditionalInfo]: displayAdditionalInfo,
        })}
      >
        {house?.houseType ? (
          <div className={s.subTitle}>
            {getHouseTypeLabel({ houseType: house.houseType, intl })}
          </div>
        ) : null}

        {house ? (
          <Capacity
            beds={house.numberOfBeds ?? 0}
            bedrooms={house.numberOfBedrooms ?? 0}
            bathrooms={house.numberOfBathrooms ?? 0}
            capacity={adults ?? 0}
            className={s.capacity}
            dailyMaxCapacity={house.options.studyDay.options?.capacity.max}
            corporateEveningEventMaxCapacity={
              house.options.corporateEvening.options?.capacity.max
            }
            numberOfWorkspaces={house.numberOfWorkspaces ?? 0}
            travelType={type}
          />
        ) : null}

        {houseEquipments ? (
          <ProposalHouseEquipments equipments={houseEquipments} />
        ) : null}
      </div>
    </div>
  )
}

export default ProposalInformation
